import { toggleSubMenuHandler } from './subMenuUtils'
import { signOut } from 'next-auth/react'
import { showSuccessToast } from '@/utils/toast/toast'
import { useRouter } from 'next/router'
import { useQueryClient } from '@tanstack/react-query'
import { FirstLevelMenuClickHandlerContext } from '@/components/PageHeader/PageHeader.model'
import mobileNavBarStyles from '@/components/PageHeader/styles/mobileNavBar.module.scss'
import { NextRouter } from 'next/router'

/**
 * Sets a timer to focus on the hamburger close icon.
 */
export const setTimerOnHamburgerCloseIconFocus = (): void => {
  setTimeout(() => {
    const hamburgerCloseIcon = document.getElementById(
      'hamburgerCloseIcon'
    ) as HTMLElement
    hamburgerCloseIcon?.focus()
  }, 1000)
}

/**
 * Handles user logout functionality.
 * @param router - Next.js router instance.
 * @param queryClient - React Query client instance.
 */
export const onLogoutClick = async (
  router: ReturnType<typeof useRouter>,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> => {
  await signOut({ callbackUrl: '/', redirect: false })
  showSuccessToast('Logout successful')
  router.push('/').then()
  queryClient.removeQueries()
}

/**
 * Handles user login functionality.
 * @param router - Next.js router instance.
 */
export const onLoginClick = (router: NextRouter): void => {
  if (router && typeof router.push === 'function') {
    router.push('/login').then()
  } else {
    console.error('Router is not properly initialized.')
  }
}

/**
 * Handles the click event for a first-level menu item.
 *
 * @param e - The event object triggered by the click action.
 * @param firstLevelMenuItem - The first-level menu item that was clicked.
 * @param context - The context object containing various properties and functions
 * required for handling the click event.
 */
export const firstLevelMenuClickHandler = (
  e: Event,
  firstLevelMenuItem: any,
  context: FirstLevelMenuClickHandlerContext
): void => {
  const {
    featureCountryCode,
    mainMenuRef,
    firstLevelItemIndex,
    profileMenuArrowFlag,
    setProfileMenuArrowFlag,
    setOverlayStatus,
    closeLoginModal,
    listMainMenu,
    removeEmptyParentMenus,
    handleSubMenuToggle,
    removeEmptyProductSuites,
    styles,
  } = context

  if (firstLevelMenuItem?.hasSubMenus && !featureCountryCode) {
    toggleSubMenuHandler(
      e,
      mainMenuRef.current ? mainMenuRef.current[firstLevelItemIndex] : null,
      {
        profileMenuArrowFlag,
        setProfileMenuArrowFlag,
        setOverlayStatus,
        closeLoginModal,
        listMainMenu,
        removeEmptyParentMenus,
        handleSubMenuToggle,
        removeEmptyProductSuites,
        styles,
      }
    )
  }
}

/**
 * Parameters for the toggleMenuCallback function.
 */
interface ToggleMenuParams {
  menuWrapperRef: React.RefObject<HTMLDivElement>
  levelOneMenuListWrapperRef: React.RefObject<HTMLDivElement>
  levelTwoMenuListWrapperRef: React.RefObject<HTMLDivElement>
  levelThreeMenuListWrapperRef: React.RefObject<HTMLDivElement>
  pageHeaderStyles: any
}

/**
 * Toggles the visibility of the menu and updates its state.
 *
 * @param params - Object containing references and styles for the menu.
 */
export const toggleMenuCallback = (params: ToggleMenuParams): void => {
  const {
    menuWrapperRef,
    levelOneMenuListWrapperRef,
    levelTwoMenuListWrapperRef,
    levelThreeMenuListWrapperRef,
    pageHeaderStyles,
  } = params

  if (typeof window !== 'undefined') {
    const menuWrapper = menuWrapperRef.current as any
    const levelOneMenuListWrapper = levelOneMenuListWrapperRef.current as any
    const levelTwoMenuListWrapper = levelTwoMenuListWrapperRef.current as any
    const levelThreeMenuListWrapper =
      levelThreeMenuListWrapperRef.current as any
    const isVisibleClass = 'isVisible'
    const isActiveClass = 'is-active'

    menuWrapper.classList.toggle(isVisibleClass)
    menuWrapper.style.display = 'block'

    if (!menuWrapper.classList.contains(isVisibleClass)) {
      levelOneMenuListWrapper.style.display = 'none'
      levelTwoMenuListWrapper.style.display = 'none'
      levelThreeMenuListWrapper.style.display = 'none'
      menuWrapper.style.display = 'none'

      const menuLinks = menuWrapper.querySelectorAll('a')
      for (const menuLink of menuLinks) {
        menuLink.classList.remove(isActiveClass)
      }

      const hamburgerIcon = document.getElementById('sm_menu_ham') as any
      hamburgerIcon.style.display = 'block'

      const hamburgerCloseIcon = document.getElementById(
        'hamburgerCloseIcon'
      ) as any
      hamburgerCloseIcon.style.display = 'none'

      const mainMenu = document.getElementById('showOverlay') as any
      mainMenu.style.display = 'none'
      mainMenu.classList.remove(pageHeaderStyles.darkMode)
    } else {
      const hamburgerIcon = document.getElementById('sm_menu_ham') as any
      hamburgerIcon.style.display = 'none'

      const hamburgerCloseIcon = document.getElementById(
        'hamburgerCloseIcon'
      ) as any
      hamburgerCloseIcon.style.display = 'block'
      hamburgerCloseIcon.focus()

      const mainMenu = document.getElementById('showOverlay') as any
      mainMenu.style.display = 'block'
      mainMenu.classList.add(pageHeaderStyles.darkMode)
    }
  }
}

/**
 * Handles the callback for toggling back to the third level menu.
 * @param levelThreeMenuListWrapperRef - Reference to the level three menu list wrapper element.
 */
export const togglebackThirdLevelCallback = (
  levelThreeMenuListWrapperRef: React.RefObject<HTMLDivElement>
): void => {
  if (typeof window !== 'undefined') {
    const isActiveClass = 'is-active'
    const parent = levelThreeMenuListWrapperRef.current as any

    parent.style = ''
    parent.previousElementSibling
      ?.querySelector('.is-active')
      ?.classList.remove(isActiveClass)
    parent.previousElementSibling
      ?.querySelector(`ul.${mobileNavBarStyles.documentPages}`)
      ?.classList.remove(`${mobileNavBarStyles.documentPages}`)

    setTimerOnHamburgerCloseIconFocus()
  }
}
